// utils/userRoleHelper.ts
import { h } from 'vue';
import { getUserRole, getControlParam } from '@/api/userApi';
import config from '@/config/user.env';
import { MdMessage } from 'mdesign3';
// import { logout } from '@/utils';

/**
 * 用户角色解析工具类
 */
export class UserRoleHelper {
  /**
   * 解析用户角色并进行角色分组聚合
   * @param apmUser 用户编码（如 'U12345'）
   * @returns Promise<IAccountData[] | null>
   *   - 成功且有角色 → 返回处理后的角色列表
   *   - 无角色 / 接口失败 → 返回 null
   */
  static async resolveUserRoles(
    apmUser: string | undefined,
  ): Promise<any[] | null> {
    if (apmUser === undefined || apmUser === null || apmUser.trim() === '') {
      return null;
    }

    try {
      // Step 1: 获取用户角色
      const roleParams = {
        userCode: apmUser,
        positionCode: 'main',
        applicationCode: config.applicationCode,
      };

      const rolesRes = await getUserRole(roleParams);

      // 无角色数据
      if (!rolesRes?.data || !Array.isArray(rolesRes.data) || rolesRes.data.length === 0) {
        return null;
      }

      // 非成功状态
      if (rolesRes.code !== '0') {
        return null;
      }

      const accountDatas = rolesRes.data;

      // Step 2: 获取角色分组配置
      let roleList: any[] = [];
      try {
        const groupParams = {
          applicationCode: config.applicationCode,
          code: 'EL_ROLE_GROUP',
        };
        const groupRes = await getControlParam(groupParams);
        const resData = groupRes?.data?.value;
        if (resData) {
          const roleGroupResult = JSON.parse(resData);
          if (Array.isArray(roleGroupResult)) {
            const roleGroupList = roleGroupResult.map((item: any) =>
              (item.roleGroupValue || []).map((e: any) => ({
                ...e,
                roleGroupCode: item.roleGroupCode,
                roleGroupName: item.roleGroupName,
              }))
            );
            roleList = roleGroupList.flat();
          }
        }
      } catch (err) {
        // 角色分组配置加载失败，不影响主流程，视为无分组
        roleList = [];
      }

      // Step 3: 合并分组逻辑
      const roleCodeArr = roleList.map((item: any) => item.roleCode);
      let tableData: any[] = [];

      if (roleCodeArr.length > 0) {
        const innerGroup = accountDatas.filter((item: any) => roleCodeArr.includes(item.roleCode));
        const outerGroup = accountDatas.filter((item: any) => !roleCodeArr.includes(item.roleCode));

        if (innerGroup.length > 0) {
          const enrichedInnerGroup = innerGroup.map((item: any) => {
            const matched = roleList.find((r: any) => r.roleCode === item.roleCode);
            return {
              ...item,
              roleName: matched?.roleGroupName || item.roleName,
              roleGroupCode: matched?.roleGroupCode,
            };
          });

          // 按 roleGroupCode 分组
          const grouped = enrichedInnerGroup.reduce((acc: Record<string, any[]>, cur) => {
            const key = cur.roleGroupCode || '__no_group__';
            if (!acc[key]) acc[key] = [];
            acc[key].push(cur);
            return acc;
          }, {});

          let uniquesGroup: any[] = [];
          Object.values(grouped).forEach((group: any[]) => {
            const uniqueUsers = [...new Set(group.map((u) => u.userCode))];
            const merged = uniqueUsers.map((userCode) => {
              const userRoles = group.filter((r) => r.userCode === userCode);
              const allRoleCodes = userRoles.map((r) => r.roleCode).join(',');
              return {
                ...userRoles[0],
                roleCode: allRoleCodes, // 合并多个角色码
              };
            });
            uniquesGroup.push(...merged);
          });

          tableData = [...uniquesGroup, ...outerGroup];
        } else {
          tableData = [...accountDatas];
        }
      } else {
        tableData = [...accountDatas];
      }

      return tableData;
    } catch (error) {
      console.error('resolveUserRoles failed:', error);
      return null;
    }
  }

  /**
   * 显示无角色提示信息，带跳转链接
   */
  static showNoRoleMessage() {
    MdMessage.error({
      message: h('span', [
        '您当前没有系统角色，请',
        h('span', {
          style: 'color: #409EFF; cursor: pointer; text-decoration: underline;',
          onClick: () => {
            const envMap = {
                'PROD': '',
                'VER': '-ver',
                'UAT': '-uat'
            };
            // 修复 TS 报错：断言环境变量为 envMap 的键名类型
            const envKey = import.meta.env.VITE_ENV as keyof typeof envMap;
            // 使用空值合并运算符简化逻辑
            const envSuffix = envMap[envKey] ?? '-uat';
            const linkUrl = `https://wb${envSuffix}.annto.com/apply/index.html#/index?applicationCode=APP7961810531930112&tenantCode=annto`;
            window.open(linkUrl, '_blank');
          }
        }, '登录安得权限系统'),
        '进行权限申请'
      ]),
      duration: 5000, // 持续时间为 0，表示不会自动关闭
    });
  }
}
